import React, { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { PublicClientApplication } from "@azure/msal-browser";
import axios from "../utils/axiosConfig";
import { Button, TextField, Container, Typography, Box, Chip, Snackbar, Alert, Grid, Avatar, Autocomplete, Select, MenuItem, FormControl, InputLabel, ListItemIcon } from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import SendIcon from "@mui/icons-material/Send";
import DeleteIcon from "@mui/icons-material/Delete";
import DescriptionIcon from "@mui/icons-material/Description";
import SaveIcon from "@mui/icons-material/Save";
import AddIcon from "@mui/icons-material/Add";
import "react-quill/dist/quill.snow.css";
import ReactQuill, { Quill } from "react-quill";
import QuillResizeModule from "quill-resize-module";
import { textFieldStyles } from "../styles/textFieldStyles";

import { actionButtonStyles } from "../styles/actionButton";
import BreadcrumbNav from "../components/BreadcrumbNav";
import FileAutocomplete from "./FileAutocomplete";
import CustomAutocomplete from "./CustomAutocomplete";

import { fetchFiles } from "../redux/actions/fileActions";
import { fetchRelationships } from "../redux/actions/relationshipActions";
import { replaceTags } from "../utils/tagReplacer";
import { useWorkflowPrompt } from "../contexts/WorkflowPromptContext";

// Register style-based attributors instead of class-based for better HTML rendering
// This ensures alignment, fonts, and other formatting use inline styles
const AlignStyle = Quill.import('attributors/style/align');
const FontStyle = Quill.import('attributors/style/font');
const SizeStyle = Quill.import('attributors/style/size');
const Parchment = Quill.import('parchment');

// Configure custom fonts - 15 popular Word-compatible fonts
// Font names must match exactly (case-sensitive in Quill, but backend will handle mapping)
FontStyle.whitelist = [
  'Arial',
  'Times New Roman',
  'Calibri',
  'Georgia',
  'Verdana',
  'Courier New',
  'Tahoma',
  'Trebuchet MS',
  'Comic Sans MS',
  'Impact',
  'Consolas',
  'Lucida Console',
  'Palatino',
  'Garamond',
  'Book Antiqua'
];

// Configure custom font sizes - common sizes used in documents
SizeStyle.whitelist = ['10px', '11px', '12px', '14px', '16px', '18px', '20px', '24px', '28px', '32px', '36px', '48px'];

// Configure line height (line spacing) - common values used in documents
// Scope BLOCK so it applies to entire paragraphs, not just selected text
const LineHeightStyle = new Parchment.Attributor.Style('lineHeight', 'line-height', {
  scope: Parchment.Scope.BLOCK,
  whitelist: ['1.0', '1.15', '1.5', '2.0', '2.5']
});

Quill.register(AlignStyle, true);
Quill.register(FontStyle, true);
Quill.register(SizeStyle, true);
Quill.register(LineHeightStyle, true);

// Enable resize module for images/media (Quill 2 compatible)
Quill.register("modules/resize", QuillResizeModule);
const msalConfig = {
  auth: {
    clientId: "4cff4091-1d1b-488f-be82-62d272df48f2",
    authority: "https://login.microsoftonline.com/common",
    clientSecret: "3UC8Q~P1~WXiJIj6DnnkFC4Tcwg2c0fL6NXTGdl~",
    redirectUri: window.location.origin + "/",
  },
};
const SendEmail = () => {
  const { t } = useTranslation(['common', 'pages', 'forms', 'buttons', 'messages']);
  const { addPendingPrompt } = useWorkflowPrompt();

  const dispatch = useDispatch();
  // const files = useSelector((state) => state.fileList.files || []);
  const location = useLocation();
  const [recipient, setRecipient] = useState([]); // Now an array for multiple recipients
  const [cc, setCc] = useState([]); // CC state
  const [bcc, setBcc] = useState([]); // BCC state
  const [subject, setSubject] = useState("");
  const [content, setContent] = useState("");
  const [hasUserEdited, setHasUserEdited] = useState(false);
  const [attachments, setAttachments] = useState(location.state?.attachments || []);

  const { user } = useSelector((state) => state.userLoaded);


  const [msalInstance, setMsalInstance] = useState(null);
  const navigate = useNavigate();
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [userRole, setUserRole] = useState(null);
  const [from, setFrom] = useState(user || null); // Initialize with the logged-in user

  const [filteredClients, setFilteredClients] = useState([]); // State to hold filtered clients

  const [templateValue, setTemplateValue] = useState("")
  const [emailTemplates, setEmailTemplates] = useState([]); // Dynamic templates from API
  const [selectedTemplate, setSelectedTemplate] = useState(null); // Store selected template object
  const [availableTags, setAvailableTags] = useState([]); // Store tags from database

  const files = useSelector((state) => state.fileList.files || []);
  const relationships = useSelector(
    (state) => state.relationshipList.relationships || []
  );

  const [selectedCase, setSelectedCase] = useState(null);

  const [draftId, setDraftId] = useState(null);
  const [isDraft, setIsDraft] = useState(false);
  const draftCaseIdRef = React.useRef(null);
  const draftLoadedRef = React.useRef(false);

  useEffect(() => {
    // Update selectedCase if files are loaded after component mount and we have a caseId
    if (files.length > 0 && location.state?.caseId) {
      const fileMatch = files.find((file) => file._id === location.state.caseId);
      if (fileMatch) {
        setSelectedCase(fileMatch);
        
        // If we have a case, also update filteredClients based on that case
        if (fileMatch.parties && fileMatch.parties.length > 0) {
          setFilteredClients(fileMatch.parties.map((member) => member.name));
        }
      }
    }
  }, [files, location.state]);

  // Load draft if navigated with draftId
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const draftIdParam = params.get("draftId");
    if (draftIdParam && !draftLoadedRef.current) {
      setIsDraft(true);
      setDraftId(draftIdParam);
      draftLoadedRef.current = true;
      axios.get(`/api/email/draft/${draftIdParam}`).then(res => {
        const draft = res.data;
        console.log("Draft data loaded:", draft);
        // Fix: Ensure 'from' is a valid user/relationship object with email
        let loadedFrom = draft.from;
        if (loadedFrom && !loadedFrom.email) {
          // Try to resolve from relationships by _id
          const rel = relationships.find(r => r._id === loadedFrom._id);
          if (rel) loadedFrom = rel;
          // Fallback to user if still no email
          if (!loadedFrom.email && user) loadedFrom = user;
        }
        setFrom(loadedFrom);
        
        // Set recipient as object if possible
        let rec = draft.recipient || draft.manualRecipient || "";
        if (typeof rec === "string") {
          // Try to find in relationships
          const rel = relationships.find(r => r._id === rec || r.email === rec);
          setRecipient(rel || rec);
        } else {
          setRecipient(rec);
        }
        
        // Set CC and BCC as objects if possible - same logic as recipient
        const resolveEmails = (arr) =>
          (arr || []).map(val => {
            if (typeof val === "string") {
              const rel = relationships.find(r => r._id === val || r.email === val);
              return rel || val;
            }
            return val;
          });
        
        console.log("Resolving CC from:", draft.cc);
        const resolvedCc = resolveEmails(draft.cc);
        console.log("Resolved CC to:", resolvedCc);
        setCc(resolvedCc);
        
        console.log("Resolving BCC from:", draft.bcc);
        const resolvedBcc = resolveEmails(draft.bcc);
        console.log("Resolved BCC to:", resolvedBcc);
        setBcc(resolvedBcc);
        
        setSubject(draft.subject || "");
        setContent(draft.content || "");
        setHasUserEdited(true);
        
        // Handle case selection
        if (draft.caseId) {
          console.log("Draft caseId found:", draft.caseId);
          draftCaseIdRef.current = draft.caseId;
          if (files.length > 0) {
            console.log("Files available, searching for case:", files);
            const caseObj = files.find(f => f._id === draft.caseId);
            console.log("Case object found:", caseObj);
            setSelectedCase(caseObj || null);
          } else {
            console.log("Files not loaded yet, caseId stored in ref");
          }
        } else {
          console.log("No caseId in draft");
        }
        
        // Handle template selection - restore template object if available
        if (draft.templateValue) {
          console.log("Template value found:", draft.templateValue);
          setTemplateValue(draft.templateValue);
          // Try to find the template object from emailTemplates
          // Note: emailTemplates might not be loaded yet, so we'll handle this in a useEffect
          const template = emailTemplates.find(t => t._id === draft.templateValue);
          if (template) {
            setSelectedTemplate(template);
          }
        } else {
          console.log("No template value in draft");
          setSelectedTemplate(null);
        }
      }).catch(error => {
        console.error("Error loading draft:", error);
      });
    }
  }, []); // Remove dependencies to prevent infinite loop

  useEffect(() => {
    if (draftCaseIdRef.current && files.length > 0) {
      console.log("Files loaded, checking for case with ID:", draftCaseIdRef.current);
      console.log("Available files:", files);
      const caseObj = files.find(f => f._id === draftCaseIdRef.current);
      console.log("Case object found in files:", caseObj);
      setSelectedCase(caseObj || null);
      draftCaseIdRef.current = null; // Clear after use
    }
  }, [files]);

  // Update selectedCase when files load and location.state.caseId exists
  useEffect(() => {
    const locationCaseId = location.state?.caseId;
    console.log('🔍 useEffect for selectedCase:', { locationCaseId, filesCount: files.length, currentSelectedCase: selectedCase?._id });
    if (locationCaseId && files.length > 0) {
      const caseFile = files.find((file) => file._id === locationCaseId);
      console.log('🔍 Case file search result:', caseFile ? { _id: caseFile._id, name: caseFile.name, fileNo: caseFile.fileNo } : 'not found');
      if (caseFile && (!selectedCase || selectedCase._id !== caseFile._id)) {
        setSelectedCase(caseFile);
        console.log('✅ Set selectedCase from location.state:', caseFile.name || caseFile.fileNo);
      } else if (!caseFile) {
        console.warn('⚠️ Case file not found in files array. caseId:', locationCaseId);
      }
    } else {
      console.warn('⚠️ Cannot set selectedCase:', { locationCaseId, filesCount: files.length });
    }
  }, [files, location.state?.caseId, selectedCase]);


  useEffect(() => {
    if (user) {
      console.log("User...", user);
      setUserRole(user.role);
      setFrom(user);
    }
  }, [user]);


  useEffect(() => {

    dispatch(fetchFiles());

  }, [dispatch]);


  useEffect(() => {
    dispatch(fetchRelationships());
  }, [dispatch]);

  // Fetch email templates from API
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const response = await axios.get('/api/email-templates');
        setEmailTemplates(response.data);
      } catch (error) {
        console.error('Error fetching email templates:', error);
        // Fallback to empty array if API fails
        setEmailTemplates([]);
      }
    };
    fetchTemplates();
  }, []);

  // Fix dropdown positioning to escape toolbar clipping (same as EmailTemplateTab)
  useEffect(() => {
    const fixDropdownPosition = () => {
      // Find the ReactQuill toolbar in this component's scope
      const wrapper = document.querySelector('[data-quill-component="send-email"]');
      const toolbar = wrapper 
        ? wrapper.querySelector('.ql-toolbar.ql-snow')
        : document.querySelector('.ql-toolbar.ql-snow');
      
      if (!toolbar) return;

      const expandedPickers = toolbar.querySelectorAll('.ql-picker.ql-expanded');
      expandedPickers.forEach((picker) => {
        const options = picker.querySelector('.ql-picker-options');
        if (!options) return;

        // Get button position (the clickable label)
        const pickerLabel = picker.querySelector('.ql-picker-label');
        if (!pickerLabel) return;

        const rect = pickerLabel.getBoundingClientRect();
        
        // Position dropdown using fixed positioning to escape toolbar clipping
        // Fixed positioning is relative to viewport, so getBoundingClientRect() values are correct
        options.style.position = 'fixed';
        options.style.top = `${rect.bottom + 2}px`;
        options.style.left = `${rect.left}px`;
        options.style.zIndex = '99999';
        options.style.width = 'auto';
        options.style.minWidth = `${rect.width}px`; // At least as wide as the button
        options.style.maxWidth = '300px'; // But not too wide
        options.style.whiteSpace = 'nowrap'; // Prevent text wrapping
      });
    };

    // Check for dropdown changes with MutationObserver
    const wrapper = document.querySelector('[data-quill-component="send-email"]');
    const toolbar = wrapper 
      ? wrapper.querySelector('.ql-toolbar.ql-snow')
      : document.querySelector('.ql-toolbar.ql-snow');
    
    if (!toolbar) return;

    const observer = new MutationObserver(() => {
      // Use setTimeout to let Quill finish rendering
      setTimeout(fixDropdownPosition, 10);
    });

    observer.observe(toolbar, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class'],
    });

    // Also fix on click events
    const handleClick = () => setTimeout(fixDropdownPosition, 20);
    toolbar.addEventListener('click', handleClick, true);

    // Use requestAnimationFrame for initial fix after render
    requestAnimationFrame(() => {
      setTimeout(fixDropdownPosition, 50);
    });

    return () => {
      observer.disconnect();
      toolbar.removeEventListener('click', handleClick, true);
    };
  }, [content, selectedTemplate]);

  // Fetch tags from API for dynamic tag replacement
  useEffect(() => {
    const fetchTags = async () => {
      try {
        const response = await axios.get('/api/email-templates/tags/all');
        setAvailableTags(response.data);
      } catch (error) {
        console.error('Error fetching tags:', error);
        setAvailableTags([]);
      }
    };
    fetchTags();
  }, []);

  // Restore selectedTemplate when emailTemplates are loaded and we have a templateValue
  useEffect(() => {
    if (templateValue && emailTemplates.length > 0 && !selectedTemplate) {
      const template = emailTemplates.find(t => t._id === templateValue);
      if (template) {
        setSelectedTemplate(template);
      }
    }
  }, [templateValue, emailTemplates, selectedTemplate]);


  useEffect(() => {
    const instance = new PublicClientApplication(msalConfig);
    setMsalInstance(instance);
  }, []);

  // useEffect(() => {
  //     if (user && relationships) {
  //       const defaultMember = relationships.find((rel) => rel._id === user.relationship);
  //       if (defaultMember) {

  //         setFrom([defaultMember]);
  //       }
  //     }
  //   }, [user, relationships]);



  const login = async () => {
    try {
      const loginResponse = await msalInstance.loginPopup({
        scopes: ["User.Read", "Mail.Send"],
      });
    } catch (error) {
      console.error("Login failed:", error);
      throw error;
    }
  };

  const getAccessToken = async () => {
    if (!msalInstance) {
      throw new Error("MSAL instance is not initialized");
    }
    await msalInstance.initialize();
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length === 0) {
      await login();
    }
    const request = {
      scopes: ["Mail.Send"],
      account: msalInstance.getAllAccounts()[0],
    };

    try {
      const response = await msalInstance.acquireTokenSilent(request);
      return response.accessToken;
    } catch (error) {
      if (error.name === "InteractionRequiredAuthError") {
        const interactiveResponse = await msalInstance.acquireTokenPopup(request);
        return interactiveResponse.accessToken;
      } else {
        throw error;
      }
    }
  };

  const handleFileChange = (event) => {
    const newFiles = Array.from(event.target.files);
    setAttachments((prevAttachments) => [...prevAttachments, ...newFiles]);
  };

  const removeAttachment = (fileToRemove) => {
    setAttachments((prevAttachments) =>
      prevAttachments.filter((file) => file !== fileToRemove)
    );
  };

  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result.split(",")[1]);
      reader.onerror = (error) => reject(error);
    });
  };

  const isBase64 = (str) => {
    if (typeof str !== 'string') {
      return false;
    }
    const base64Regex = /^(?:[A-Za-z0-9+/]{4})*?(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/;
    return base64Regex.test(str);
  };



  const handleSendEmail = async () => {
    try {
      const accessToken = await getAccessToken();

      // Normalize recipients to array of emails
      const normalizeRecipientEmails = (arr) =>
        (arr || [])
          .filter(item => typeof item === "string" ? item.trim() !== "" : !!item)
          .map((item) => {
            if (typeof item === "string") {
              return item.trim();
            } else if (item && item.email) {
              return item.email;
            } else if (item && item._id) {
              const rel = relationships.find((rel) => rel._id === item._id);
              return rel?.contacts?.[0]?.email || null;
      }
            return null;
          })
          .filter(Boolean);

      const recipientEmails = normalizeRecipientEmails(recipient);
      if (!recipientEmails.length) {
        setSnackbarMessage(t('messages:please_enter_recipient'));
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
        return;
      }

      // Helper to normalize emails for CC/BCC
      const normalizeEmails = (arr) =>
        (arr || [])
          .filter(item => typeof item === "string" ? item.trim() !== "" : !!item)
          .map((item) => {
            if (typeof item === "string") {
              return { emailAddress: { address: item.trim() } };
            } else if (item && item.email) {
              return { emailAddress: { address: item.email } };
            } else if (item && item._id) {
              const rel = relationships.find((rel) => rel._id === item._id);
              return rel?.contacts?.[0]?.email
                ? { emailAddress: { address: rel.contacts[0].email } }
                : null;
            }
            return null;
          })
          .filter(Boolean);

      // Replace tags in content and subject with latest context before sending
      // Ensure we have the latest data (files and relationships should be loaded by now)
      const context = getContext();
      
      // Double-check selectedCase if we have location.state.caseId but selectedCase is null
      if (!context.selectedCase && location.state?.caseId && files.length > 0) {
        const caseFile = files.find((file) => file._id === location.state.caseId);
        if (caseFile) {
          context.selectedCase = caseFile;
          setSelectedCase(caseFile);
          console.log('✅ Found case file during send:', caseFile.name || caseFile.fileNo);
        } else {
          console.warn('⚠️ Case file not found. caseId:', location.state.caseId, 'Files count:', files.length);
        }
      } else if (!context.selectedCase) {
        console.warn('⚠️ selectedCase is null. location.state.caseId:', location.state?.caseId, 'files.length:', files.length);
      }
      
      const finalContent = replaceTags(content, context, availableTags);
      const finalSubject = replaceTags(subject, context, availableTags);

      let emailPayload = {
        message: {
          subject: finalSubject,
          body: {
            contentType: "HTML",
            content: finalContent,
          },
          toRecipients: recipientEmails.map(address => ({ emailAddress: { address } })),
          ccRecipients: normalizeEmails(cc),
          bccRecipients: normalizeEmails(bcc),
        },
      };

      // console.log("email payload is", emailPayload)

      if (attachments.length > 0) {
        const attachmentPromises = attachments.map(async (attachment) => {
          let base64Content = attachment.base64Content;
          if (!isBase64(base64Content)) {
            base64Content = await convertFileToBase64(attachment);
            console.log(`Base64 content for ${attachment.name}:`, base64Content); // Log base64 content
            return {
              "@odata.type": "#microsoft.graph.fileAttachment",
              name: attachment.name,
              contentType: attachment.type,
              contentBytes: base64Content,
            };
          }
          return {
            "@odata.type": "#microsoft.graph.fileAttachment",
            name: attachment.file.name,
            contentType: attachment.file.type,
            contentBytes: base64Content,
          };
        });
        emailPayload.message.attachments = await Promise.all(attachmentPromises);
      }
      console.log("Email payload:", emailPayload);
      try {
        const response = await axios.post(
          "/api/email/send",
          {
            accessToken,
            emailPayload,
            emailTemplateId: templateValue || null,
            caseId: selectedCase?._id || location.state?.caseId || null,
            userId: user?._id || null,
          }
        );
        setSnackbarMessage(t('messages:email_sent_successfully'));

        // Check for pending workflows and add to prompt queue
        if (response.data?.pendingWorkflows && Array.isArray(response.data.pendingWorkflows)) {
          response.data.pendingWorkflows.forEach(pending => {
            addPendingPrompt(pending);
          });
        }

        // Prepare document arrays for all attachments
        let documentName = [];
        let documentData = [];
        let documentMimeType = [];
        if (attachments.length > 0) {
          for (const fileObj of attachments) {
            const file = fileObj.file || fileObj;
            documentName.push(file.name);
            documentMimeType.push(file.type);
            let base64Content = fileObj.base64Content;
            if (!isBase64(base64Content)) {
              base64Content = await convertFileToBase64(file);
            }
            documentData.push(base64Content);
          }
        }

        // Prepare recipient data for database
        // Split recipients into relationship IDs and manual emails
        const recipientIds = recipient
          .filter((rec) => typeof rec === "object" && rec._id)
          .map((rec) => rec._id);
        const manualRecipients = recipient
          .filter((rec) => typeof rec === "string")
          .map((rec) => rec);

        // Save email with replaced content/subject
        let emailData = {
          from: from.relationship,
          recipient: recipientIds,
          subject: finalSubject,
          content: finalContent,
          documentName,
          documentMimeType,
          documentData,
          manualRecipient: manualRecipients,
          caseId: selectedCase?._id || null
        }

        // Now send to your backend:
        try {
          console.log("email data saved is ", emailData)
          await axios.post("/api/email/save", emailData);
          // If this was a draft, remove it after successful save
          if (draftId) {
            await axios.post("/api/email/remove-draft", { draftId });
            setDraftId(null);
            setIsDraft(false);
            console.log("Draft removed after send.");
          }
        } catch (saveError) {
          setSnackbarMessage(t('messages:email_sent_save_failed'));
          setSnackbarSeverity("warning");
          setSnackbarOpen(true);
          return;
        }

        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        setTimeout(() => {
          navigate(-1); // Navigate back to the previous page after 2 seconds
        }, 2000);
      } catch (error) {
        console.error("Error sending email 2:", error);
        setSnackbarMessage(t('messages:failed_to_send_email'));
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error("Error sending email:", error);
      setSnackbarMessage(t('messages:failed_to_send_email'));
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  // Save draft handler
  const handleSaveDraft = async () => {
    try {
      const payload = {
        _id: draftId,
        from: (user && (user.relationship || user._id)) || (from && (from.relationship || from._id)) || undefined,
        recipient: recipient.filter((rec) => typeof rec === "object" && rec._id).map((rec) => rec._id),
        manualRecipient: recipient.filter((rec) => typeof rec === "string"),
        subject,
        content,
        cc: cc.map(c => (typeof c === "object" ? c._id || c.email : c)),
        bcc: bcc.map(b => (typeof b === "object" ? b._id || b.email : b)),
        caseId: selectedCase?._id || null,
        templateValue,
      };
      console.log("Saving draft with payload:", payload);
      const res = await axios.post("/api/email/draft", payload);
      setDraftId(res.data._id);
      setSnackbarMessage(t('messages:draft_saved_successfully'));
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Error saving draft:", error);
      setSnackbarMessage(t('messages:failed_to_save_draft'));
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  // In handleSendEmail, after successful send:
  // if (draftId) await axios.post("/api/email/remove-draft", { draftId });
  // setDraftId(null); setIsDraft(false);

  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }], // All heading levels (h1-h6) + normal text
      [{ 'font': FontStyle.whitelist }], // Custom font list
      [{ 'size': SizeStyle.whitelist }], // Custom font sizes
      [{ 'lineHeight': ['1.0', '1.15', '1.5', '2.0', '2.5'] }], // Line spacing options
      [{ 'color': [] }, { 'background': [] }], // Text color and background color (default palette)
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      ['bold', 'italic', 'underline', 'strike', 'blockquote'],
      [{ 'indent': '-1' }, { 'indent': '+1' }],
      [{ 'align': [] }],
      ['link', 'image', 'code-block'],
    ],
    resize: {
      modules: ['Resize', 'DisplaySize'],
    },
  };



  const filteredFiles = files.filter((file) => {
    if (userRole === "Manager") {
      return true;
    } else if (userRole === "Responsible Lawyer") {
      return file.officeMembers.some(
        (member) =>
          member.member._id === user.relationship &&
          member.role === "Responsible Lawyer"
      );
    } else if (userRole === "Assistant Lawyer") {
      return file.officeMembers.some(
        (member) =>
          member.member._id === user.relationship &&
          member.role === "Treating Lawyer"
      );
    }
    return false;
  });

  const handleCaseChange = (event, newValue) => {
    setSelectedCase(newValue);
    if (newValue) {
      const selectedFile = files.filter((file) => file._id === newValue._id)[0];

      const clients = selectedFile.parties.map((member) => member.name);
      //  const clients = selectedFile.parties; // Each party should have an email property
      // console.log("clients are", clients)

      setFilteredClients(clients)
      // setRecipient(newValue); // Set the first client as the recipient
    }
  }


  // Get current context for tag replacement
  const getContext = () => {
    // Handle recipient array - use first recipient for replacement
    let recipientObj = Array.isArray(recipient) ? (recipient.length > 0 ? recipient[0] : null) : recipient;
    
    // If recipient is a string (ID or email), try to find the full object in relationships
    if (typeof recipientObj === 'string' && relationships.length > 0) {
      // Normalize the recipient string for matching
      const recipientStr = recipientObj.trim();
      const recipientLower = recipientStr.toLowerCase();
      
      // Try to find by ID first
      let foundRecipient = relationships.find(r => r._id === recipientStr || r._id?.toString() === recipientStr);
      
      // If not found by ID, try to find by email (case-insensitive, with normalization)
      if (!foundRecipient) {
        foundRecipient = relationships.find(r => {
          if (!r.email) return false;
          const rEmail = r.email.trim().toLowerCase();
          return rEmail === recipientLower || rEmail.replace(/[._-]/g, '') === recipientLower.replace(/[._-]/g, '');
        });
      }
      
      // Also check contacts array for email matches
      if (!foundRecipient) {
        foundRecipient = relationships.find(r => {
          if (!r.contacts || r.contacts.length === 0) return false;
          return r.contacts.some(c => {
            if (!c.email) return false;
            const cEmail = c.email.trim().toLowerCase();
            return cEmail === recipientLower || cEmail.replace(/[._-]/g, '') === recipientLower.replace(/[._-]/g, '');
          });
        });
      }
      
      if (foundRecipient) {
        recipientObj = foundRecipient;
        console.log('✅ Resolved recipient from relationships:', {
          original: recipientStr,
          resolved: foundRecipient.firstName || foundRecipient.businessName || foundRecipient.email,
        });
      } else {
        console.warn('⚠️ Could not resolve recipient from relationships. Email/ID:', recipientStr, 'Relationships count:', relationships.length);
      }
    }
    
    // If recipient is null/undefined, try to get it from selectedCase parties
    if (!recipientObj && selectedCase?.parties && selectedCase.parties.length > 0) {
      const clientParty = selectedCase.parties.find(p => p.type === 'Client');
      if (clientParty?.name) {
        const nameId = typeof clientParty.name === 'object' ? clientParty.name._id : clientParty.name;
        recipientObj = relationships.find((r) => r._id === nameId || r._id?.toString() === nameId?.toString()) || clientParty.name;
      }
    }
    // Prefer full relationship from list (has email, dateOfBirth) when recipient is from case/party
    if (recipientObj && typeof recipientObj === 'object' && recipientObj._id && relationships.length > 0) {
      const fullRecipient = relationships.find((r) => r._id?.toString() === recipientObj._id?.toString());
      if (fullRecipient) recipientObj = fullRecipient;
    }
    
    // Ensure from has relationship populated if user has relationship reference
    let fromObj = from;
    
    // If from is a User object and we need Relationship fields, try to resolve it
    if (fromObj && fromObj.relationship && typeof fromObj.relationship === 'string' && relationships.length > 0) {
      const userRelationship = relationships.find(r => 
        r._id === fromObj.relationship || r._id?.toString() === fromObj.relationship
      );
      if (userRelationship) {
        // Merge user data with relationship data for complete sender info
        fromObj = { ...fromObj, ...userRelationship };
        console.log('✅ Resolved from relationship:', {
          businessName: userRelationship.businessName,
          firstName: userRelationship.firstName,
          lastName: userRelationship.lastName,
          mergedFromObj: { businessName: fromObj.businessName, firstName: fromObj.firstName, lastName: fromObj.lastName },
        });
      }
    } else if (!fromObj && user?.relationship && relationships.length > 0) {
      // If user has relationship reference but from is not set, try to find it
      const userRelationship = relationships.find(r => 
        r._id === user.relationship || r._id?.toString() === user.relationship
      );
      if (userRelationship) {
        // Merge user data with relationship data
        fromObj = { ...user, ...userRelationship };
        console.log('✅ Resolved user relationship:', {
          businessName: userRelationship.businessName,
          firstName: userRelationship.firstName,
          lastName: userRelationship.lastName,
          mergedFromObj: { businessName: fromObj.businessName, firstName: fromObj.firstName, lastName: fromObj.lastName },
        });
      } else {
        // Fallback: use user object directly (User model has firstName/lastName)
        fromObj = user;
        console.warn('⚠️ Could not resolve user relationship, using user object directly');
      }
    } else if (!fromObj) {
      // Fallback: use user object directly
      fromObj = user;
    }
    
    // Final fallback: if fromObj still doesn't have name fields, ensure we have at least user data
    if (fromObj && !fromObj.firstName && !fromObj.businessName && user) {
      fromObj = { ...fromObj, ...user };
    }
    
    // Case client (for client* placeholders) - resolve from relationships so we get email, dateOfBirth, etc.
    let clientObj = null;
    if (selectedCase?.parties && relationships.length > 0) {
      const clientParty = selectedCase.parties.find((p) => p.type === 'Client');
      if (clientParty?.name) {
        const nameId = typeof clientParty.name === 'object' ? clientParty.name._id : clientParty.name;
        clientObj = relationships.find((r) => r._id === nameId || r._id?.toString() === nameId?.toString()) || (typeof clientParty.name === 'object' ? clientParty.name : null);
      }
    }
    
    // Resolve lawyers from relationships so lawyer* email placeholders have full data
    const getOfficeMemberByRole = (role) => {
      if (!selectedCase || !Array.isArray(selectedCase.officeMembers)) return null;
      const match = selectedCase.officeMembers.find((m) => m.role === role);
      const member = match?.member;
      if (!member) return null;
      const memberId = typeof member === 'object' ? member._id : member;
      return relationships.find((r) => r._id === memberId || r._id?.toString() === memberId?.toString()) || member;
    };

    const responsibleLawyerObj = getOfficeMemberByRole('Responsible Lawyer');
    const treatingLawyerObj = getOfficeMemberByRole('Treating Lawyer');

    const context = {
      recipient: recipientObj,
      selectedCase,
      from: fromObj,
      user,
      client: clientObj,
      responsibleLawyer: responsibleLawyerObj,
      treatingLawyer: treatingLawyerObj,
    };
    
    // Debug: Verify fromObj has the data we expect
    if (fromObj) {
      console.log('🔍 Context.from object structure:', {
        businessName: fromObj.businessName,
        firstName: fromObj.firstName,
        lastName: fromObj.lastName,
        email: fromObj.email,
        hasBusinessName: !!fromObj.businessName,
        hasFirstName: !!fromObj.firstName,
        keys: Object.keys(fromObj).slice(0, 15),
      });
    }
    
    // Debug logging to help identify issues
    console.log('🔍 Tag Replacement Context:', {
      recipient: recipientObj,
      recipientType: typeof recipientObj,
      recipientIsObject: typeof recipientObj === 'object' && recipientObj !== null,
      recipientKeys: recipientObj && typeof recipientObj === 'object' ? Object.keys(recipientObj).slice(0, 10) : 'N/A',
      recipientEmail: recipientObj && typeof recipientObj === 'object' ? recipientObj.email : recipientObj,
      relationshipsCount: relationships.length,
      selectedCase: selectedCase ? {
        _id: selectedCase._id,
        name: selectedCase.name,
        fileNo: selectedCase.fileNo,
        type: selectedCase.type,
      } : null,
      from: fromObj ? {
        firstName: fromObj.firstName,
        lastName: fromObj.lastName,
        businessName: fromObj.businessName,
        email: fromObj.email,
        hasRelationship: !!fromObj.relationship,
        keys: Object.keys(fromObj).slice(0, 10),
      } : null,
      user: user ? {
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        hasRelationship: !!user.relationship,
      } : null,
      availableTagsCount: availableTags.length,
    });
    
    return context;
  };

  // Handle template selection - store template without replacing tags (lazy replacement)
  const handleTemplateSelect = (template) => {
    if (!template) {
      setTemplateValue("");
      setSelectedTemplate(null);
      return;
    }
    if (template.isCreateNew) {
      navigate("/settings", { state: { activeTab: 0 } });
      return;
    }

    setTemplateValue(template._id || template.value || "");
    setSelectedTemplate(template);
    setHasUserEdited(false);
    
    // Set raw template content/subject without replacement
    // Tags will be replaced on send/preview with latest context
    setContent(template.content || '');
    setSubject(template.subject || '');
  };

  // Auto-refresh template-based content/subject when context changes,
  // as long as the user hasn't manually edited the fields in this session.
  useEffect(() => {
    if (!selectedTemplate || hasUserEdited) return;

    const context = getContext();
    const replacedContent = replaceTags(selectedTemplate.content || '', context, availableTags);
    const replacedSubject = replaceTags(selectedTemplate.subject || '', context, availableTags);

    setContent(replacedContent);
    setSubject(replacedSubject);
  }, [
    selectedTemplate,
    recipient,
    selectedCase,
    from,
    user,
    relationships,
    files,
    availableTags,
    hasUserEdited,
  ]);

  const [breadcrumbs, setBreadcrumbs] = useState([
    { label: t('common:home'), href: "/" },
    { label: t('common:emails'), href: "/emails" },
  ]);

  useEffect(() => {
    // Try to get caseId from location.state or query params
    const caseId = location.state?.caseId || new URLSearchParams(location.search).get("caseId");
    if (caseId && files.length > 0) {
      const caseFile = files.find((file) => file._id === caseId);
      if (caseFile) {
        setBreadcrumbs([
          { label: t('common:home'), href: "/" },
          { label: t('common:emails'), href: "/emails" },
          { label: caseFile.name, href: `/files/${caseFile._id}/overview` }
        ]);
      }
    } else {
      setBreadcrumbs([
        { label: t('common:home'), href: "/" },
        { label: t('common:emails'), href: "/emails" }
      ]);
    }
  }, [location, files]);

  // If you want to add more dynamic breadcrumbs, you can use setBreadcrumbs as in DocumentUpload.js

  return (
    <Container >
       <BreadcrumbNav
        title={t('pages:emails.send_email')}
        breadcrumbs={breadcrumbs}
        showBreadcrumbs={true}
        sx={{
          ml: 4,
        }}
      />
      <Container maxWidth="lg" sx={{ backgroundColor: "white", boxShadow: "0px 4px 16px rgba(0,0,0,0.013)", pt: 2, marginBottom: 8, marginTop: 2 }}>
     
      <Grid container spacing={2} alignItems="center">
        {/* From Field */}
        <Grid item xs={12} md={4}>
          <Autocomplete
            options={[user]}
            value={from || null}
            onChange={(event, newValue) => {
              if (newValue) {
                setFrom(newValue);
              } else {
                setFrom(null);
              }
            }}
            getOptionLabel={(option) =>
              option?.name || `${option.firstName || ""} ${option.middleName || ""} ${option.lastName || ""}${option.businessName || ""}`
            }
            renderOption={(props, option) => (
              <Box component="li" {...props} display="flex" alignItems="center">
                <Avatar
                  src={option?.avatar || ""}
                  alt={option?.name || ""}
                  sx={{ width: "1rem", height: "1rem", marginRight: 1 }}
                />
                <Typography sx={{ fontSize: "10.5px", lineHeight: 1 }}>
                  {`${option.firstName || ""} ${option.middleName || ""} ${option.lastName || ""}${option.businessName || ""}`}
                </Typography>
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('forms:from')}
                size="small"
                sx={textFieldStyles()}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: from && (
                    <Avatar
                      src={from.avatar || ""}
                      alt={from.name || ""}
                      sx={{ width: "1rem", height: "1rem", marginRight: 0, marginLeft: 1 }}
                    />
                  ),
                }}
              />
            )}
          />
        </Grid>
        {/* Case Field */}
        <Grid item xs={12} md={4}>
          <FileAutocomplete
            options={filteredFiles}
            value={selectedCase || null}
            onChange={handleCaseChange}
            label={t('forms:labels.case_required')}
            name="case"
          />
        </Grid>
        {/* Template Field */}
        <Grid item xs={12} md={4}>
          <Autocomplete
            options={[
              { _id: "__create_new__", name: "Create New Template", isCreateNew: true },
              ...emailTemplates,
            ]}
            value={emailTemplates.find(t => (t._id || t.value) === templateValue) || null}
            onChange={(event, newValue) => {
              handleTemplateSelect(newValue);
            }}
            getOptionLabel={(option) => option.name || option.label || ""}
            renderOption={(props, option) => (
              <Box component="li" {...props} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {option.isCreateNew && (
                  <ListItemIcon sx={{ minWidth: 24, color: "#005b92" }}>
                    <AddIcon fontSize="small" />
                  </ListItemIcon>
                )}
                <Box sx={{ color: option.isCreateNew ? "#005b92" : "inherit" }}>
                  {option.name || option.label}
                </Box>
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('forms:labels.template')}
                size="small"
                sx={textFieldStyles()}
              />
            )}
            slotProps={{
              paper: {
                sx: {
                  borderRadius: 0,
                  border: "1px solid #005b92",
                  boxShadow: "none",
                },
              },
            }}
          />
        </Grid>
      </Grid>
      {/* Recipient Field - full width, above CC */}
        <Autocomplete
        sx={{ marginTop: "1rem", marginBottom: "1rem" }}
        multiple
        freeSolo
        fullWidth
          options={filteredClients}
          value={recipient}
        filterSelectedOptions
        onChange={(event, newValue, reason, details) => {
          setRecipient(
            newValue
              .map((item) => (typeof item === "string" ? item.trim() : item))
              .filter(
                (item, idx, arr) =>
                  item &&
                  (typeof item === "string"
                    ? arr.findIndex((i) => i === item) === idx
                    : arr.findIndex((i) => i?._id === item?._id) === idx)
              )
          );
          }}
          getOptionLabel={(option) => {
          if (typeof option === "string") return option;
            return (
              option?.email ||
              option?.name ||
              `${option.firstName || ""} ${option.middleName || ""} ${option.lastName || ""}${option.businessName || ""}`
            );
          }}
          renderOption={(props, option) => (
            <Box component="li" {...props} display="flex" alignItems="center">
              {option?.avatar && (
                <Avatar
                  src={option.avatar}
                  alt={option.name || ''}
                  sx={{ width: "1rem", height: "1rem", marginRight: 1 }}
                />
              )}
              {option?.name || option?.email || `${option.firstName || ""} ${option.middleName || ""} ${option.lastName || ""}${option.businessName || ""}`}
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
            label={t('forms:labels.recipients')}
              size="small"
            placeholder={t('forms:type_select_recipients')}
              sx={textFieldStyles()}
                    />
        )}
        ChipProps={{
          size: 'small',
          sx: {
            fontSize: '8px',
            height: 18,
            borderRadius: '16px',
            padding: '0 4px',
            '.MuiChip-label': {
              padding: '0 8px',
            },
            margin: "0px"
          },
        }}
      />
      {/* CC Field */}
      <Grid item xs={6}>
        <Autocomplete
          sx={{ marginTop: "1rem" }}
          multiple
          freeSolo
          options={filteredClients}
          value={cc}
          filterSelectedOptions
          onChange={(event, newValue, reason, details) => {
            // Only allow unique, non-empty values
            setCc(
              newValue
                .map((item) => (typeof item === "string" ? item.trim() : item))
                .filter(
                  (item, idx, arr) =>
                    item &&
                    (typeof item === "string"
                      ? arr.findIndex((i) => i === item) === idx
                      : arr.findIndex((i) => i?._id === item?._id) === idx)
                )
            );
          }}
          getOptionLabel={(option) => {
            if (typeof option === "string") return option;
            return (
              option?.email ||
              option?.name ||
              `${option.firstName || ""} ${option.middleName || ""} ${option.lastName || ""}${option.businessName || ""}`
            );
          }}
          renderOption={(props, option) => (
            <Box component="li" {...props} display="flex" alignItems="center">
              {option?.avatar && (
                <Avatar
                  src={option.avatar}
                  alt={option.name || ""}
                  sx={{ width: "1rem", height: "1rem", marginRight: 1 }}
                />
              )}
              {option?.name ||
                option?.email ||
                `${option.firstName || ""} ${option.middleName || ""} ${option.lastName || ""}${option.businessName || ""}`}
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('forms:labels.cc')}
              size="small"
              placeholder={t('forms:type_select_cc')}
              sx={textFieldStyles()}
            />
          )}
          ChipProps={{
            size: 'small',
            sx: {
              fontSize: '8px',
              height: 18,
              borderRadius: '16px',
              padding: '0 4px',
              '.MuiChip-label': {
                padding: '0 8px',
              },
            },
          }}
        />
      </Grid>
      {/* BCC Field */}
      <Grid item xs={6}>
        <Autocomplete
          sx={{ marginTop: "1rem" }}
          multiple
          freeSolo
          options={filteredClients}
          value={bcc}
          filterSelectedOptions
          onChange={(event, newValue, reason, details) => {
            setBcc(
              newValue
                .map((item) => (typeof item === "string" ? item.trim() : item))
                .filter(
                  (item, idx, arr) =>
                    item &&
                    (typeof item === "string"
                      ? arr.findIndex((i) => i === item) === idx
                      : arr.findIndex((i) => i?._id === item?._id) === idx)
                )
            );
          }}
          getOptionLabel={(option) => {
            if (typeof option === "string") return option;
            return (
              option?.email ||
              option?.name ||
              `${option.firstName || ""} ${option.middleName || ""} ${option.lastName || ""}${option.businessName || ""}`
            );
          }}
          renderOption={(props, option) => (
            <Box component="li" {...props} display="flex" alignItems="center">
              {option?.avatar && (
                <Avatar
                  src={option.avatar}
                  alt={option.name || ""}
                  sx={{ width: "1rem", height: "1rem", marginRight: 1 }}
                />
              )}
              {option?.name ||
                option?.email ||
                `${option.firstName || ""} ${option.middleName || ""} ${option.lastName || ""}${option.businessName || ""}`}
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('forms:labels.bcc')}
              size="small"
              placeholder={t('forms:type_select_bcc')}
              sx={textFieldStyles()}
            />
          )}
          ChipProps={{
            size: 'small',
            sx: {
              fontSize: '8px',
              height: 18,
              borderRadius: '16px',
              padding: '0 4px',
              '.MuiChip-label': {
                padding: '0 8px',
              },
            },
          }}
        />
      </Grid>
      <TextField
        fullWidth
        margin="normal"
        label={t('forms:labels.subject')}
        sx={{ ...textFieldStyles(), margin: "1rem 0" }}
        size="small"
        value={subject}
        onChange={(e) => {
          setSubject(e.target.value);
          setHasUserEdited(true);
        }}
      />
      <Typography variant="subtitle1" gutterBottom sx={{
        fontFamily: "'Segoe UI', sans-serif",
        fontSize: "16px",
        fontWeight: 500,
      }}>
        {t('forms:labels.content')}
      </Typography>      <Box display="flex" alignItems="flex-end">
        {/* Wrap ReactQuill so dropdown JS can scope correctly */}
        <Box
          sx={{ position: 'relative', overflow: 'visible', zIndex: 1, width: '100%' }}
          data-quill-component="send-email"
        >
          {/* Custom CSS for font and size dropdown labels */}
          <style>
            {`
                      /* Toolbar: 2 rows, no horizontal scroll */
                      .ql-toolbar.ql-snow {
                        display: flex;
                        flex-wrap: wrap;
                        align-items: center;
                        gap: 4px 8px;
                        padding: 6px 8px 10px 8px;
                        position: relative;
                        overflow: visible;
                      }
                      
                      /* Toolbar groups spacing (wrap to second row when needed) */
                      .ql-toolbar.ql-snow .ql-formats {
                        display: inline-flex;
                        align-items: center;
                        margin-right: 0;
                      }

                      /* Header picker (Normal / H1–H6): auto width + short labels (P, H1–H6) */
                      .ql-snow .ql-picker.ql-header {
                        width: auto !important;
                        min-width: 52px;
                      }
                      .ql-snow .ql-picker.ql-header .ql-picker-label {
                        width: max-content;
                        padding-right: 18px;
                      }
                      .ql-snow .ql-picker.ql-header .ql-picker-label::before,
                      .ql-snow .ql-picker.ql-header .ql-picker-item::before {
                        content: 'P';
                      }
                      .ql-snow .ql-picker.ql-header .ql-picker-label[data-value="1"]::before,
                      .ql-snow .ql-picker.ql-header .ql-picker-item[data-value="1"]::before { content: 'H1'; }
                      .ql-snow .ql-picker.ql-header .ql-picker-label[data-value="2"]::before,
                      .ql-snow .ql-picker.ql-header .ql-picker-item[data-value="2"]::before { content: 'H2'; }
                      .ql-snow .ql-picker.ql-header .ql-picker-label[data-value="3"]::before,
                      .ql-snow .ql-picker.ql-header .ql-picker-item[data-value="3"]::before { content: 'H3'; }
                      .ql-snow .ql-picker.ql-header .ql-picker-label[data-value="4"]::before,
                      .ql-snow .ql-picker.ql-header .ql-picker-item[data-value="4"]::before { content: 'H4'; }
                      .ql-snow .ql-picker.ql-header .ql-picker-label[data-value="5"]::before,
                      .ql-snow .ql-picker.ql-header .ql-picker-item[data-value="5"]::before { content: 'H5'; }
                      .ql-snow .ql-picker.ql-header .ql-picker-label[data-value="6"]::before,
                      .ql-snow .ql-picker.ql-header .ql-picker-item[data-value="6"]::before { content: 'H6'; }

                      /* Font picker: auto width so "Arial" is narrow, "Times New Roman" fits */
                      .ql-snow .ql-picker.ql-font {
                        width: auto !important;
                        min-width: 50px;
                      }
                      .ql-snow .ql-picker.ql-font .ql-picker-label {
                        width: max-content;
                        max-width: 200px;
                        overflow: hidden;
                        text-overflow: ellipsis;
                        white-space: nowrap;
                        padding-right: 18px;
                      }
                      .ql-snow .ql-picker.ql-font .ql-picker-label::before {
                        display: inline-block;
                        max-width: 100%;
                        overflow: hidden;
                        text-overflow: ellipsis;
                        white-space: nowrap;
                        vertical-align: bottom;
                      }

                      /* Editor container: ensure it doesn't clip dropdowns */
                      .ql-container.ql-snow {
                        position: relative;
                        z-index: 1;
                      }

                      /* Dropdown options: JavaScript positions these with fixed positioning */
                      .ql-toolbar.ql-snow .ql-picker.ql-expanded .ql-picker-options {
                        position: fixed !important;
                        z-index: 99999 !important;
                        background-color: white !important;
                        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15) !important;
                        border: 1px solid #e0e0e0 !important;
                        border-bottom: 2px solid #d0d0d0 !important;
                        border-radius: 4px !important;
                        max-height: 200px;
                        overflow-y: auto;
                        display: block !important;
                        width: auto !important;
                        white-space: nowrap !important;
                      }

            /* Font dropdown labels - show actual font names with their fonts */
            .ql-snow .ql-picker.ql-font .ql-picker-label[data-value="Arial"]::before,
                      .ql-snow .ql-picker.ql-font .ql-picker-item[data-value="Arial"]::before {
                        content: 'Arial';
                        font-family: 'Arial', sans-serif;
                      }
                      .ql-snow .ql-picker.ql-font .ql-picker-label[data-value="Times New Roman"]::before,
                      .ql-snow .ql-picker.ql-font .ql-picker-item[data-value="Times New Roman"]::before {
                        content: 'Times New Roman';
                        font-family: 'Times New Roman', serif;
                      }
            .ql-snow .ql-picker.ql-font .ql-picker-label[data-value="Verdana"]::before,
            .ql-snow .ql-picker.ql-font .ql-picker-item[data-value="Verdana"]::before {
              content: 'Verdana';
              font-family: 'Verdana', sans-serif;
            }
            .ql-snow .ql-picker.ql-font .ql-picker-label[data-value="Courier New"]::before,
            .ql-snow .ql-picker.ql-font .ql-picker-item[data-value="Courier New"]::before {
              content: 'Courier New';
              font-family: 'Courier New', monospace;
            }
            .ql-snow .ql-picker.ql-font .ql-picker-label[data-value="Tahoma"]::before,
            .ql-snow .ql-picker.ql-font .ql-picker-item[data-value="Tahoma"]::before {
              content: 'Tahoma';
              font-family: 'Tahoma', sans-serif;
            }
            .ql-snow .ql-picker.ql-font .ql-picker-label[data-value="Trebuchet MS"]::before,
            .ql-snow .ql-picker.ql-font .ql-picker-item[data-value="Trebuchet MS"]::before {
              content: 'Trebuchet MS';
              font-family: 'Trebuchet MS', sans-serif;
            }
            .ql-snow .ql-picker.ql-font .ql-picker-label[data-value="Comic Sans MS"]::before,
            .ql-snow .ql-picker.ql-font .ql-picker-item[data-value="Comic Sans MS"]::before {
              content: 'Comic Sans MS';
              font-family: 'Comic Sans MS', sans-serif;
            }
            .ql-snow .ql-picker.ql-font .ql-picker-label[data-value="Impact"]::before,
            .ql-snow .ql-picker.ql-font .ql-picker-item[data-value="Impact"]::before {
              content: 'Impact';
              font-family: 'Impact', sans-serif;
            }
            .ql-snow .ql-picker.ql-font .ql-picker-label[data-value="Consolas"]::before,
            .ql-snow .ql-picker.ql-font .ql-picker-item[data-value="Consolas"]::before {
              content: 'Consolas';
              font-family: 'Consolas', monospace;
            }
            .ql-snow .ql-picker.ql-font .ql-picker-label[data-value="Lucida Console"]::before,
            .ql-snow .ql-picker.ql-font .ql-picker-item[data-value="Lucida Console"]::before {
              content: 'Lucida Console';
              font-family: 'Lucida Console', monospace;
            }
            .ql-snow .ql-picker.ql-font .ql-picker-label[data-value="Palatino"]::before,
            .ql-snow .ql-picker.ql-font .ql-picker-item[data-value="Palatino"]::before {
              content: 'Palatino';
              font-family: 'Palatino', serif;
            }
            .ql-snow .ql-picker.ql-font .ql-picker-label[data-value="Garamond"]::before,
            .ql-snow .ql-picker.ql-font .ql-picker-item[data-value="Garamond"]::before {
              content: 'Garamond';
              font-family: 'Garamond', serif;
            }
            .ql-snow .ql-picker.ql-font .ql-picker-label[data-value="Book Antiqua"]::before,
            .ql-snow .ql-picker.ql-font .ql-picker-item[data-value="Book Antiqua"]::before {
              content: 'Book Antiqua';
              font-family: 'Book Antiqua', serif;
            }
            
            /* Font-size picker: fixed narrow width (consistent with Document Template tab) */
            .ql-snow .ql-picker.ql-size {
              width: 60px;
            }
            .ql-snow .ql-picker.ql-size .ql-picker-label {
              width: 100%;
              text-align: left;
            }
            
            /* Size dropdown labels */
            .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="10px"]::before,
            .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="10px"]::before { content: '10px'; }
            .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="11px"]::before,
            .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="11px"]::before { content: '11px'; }
            .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="12px"]::before,
            .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="12px"]::before { content: '12px'; }
            .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="14px"]::before,
            .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="14px"]::before { content: '14px'; }
            .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="16px"]::before,
            .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="16px"]::before { content: '16px'; }
            .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="18px"]::before,
            .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="18px"]::before { content: '18px'; }
            .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="20px"]::before,
            .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="20px"]::before { content: '20px'; }
            .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="24px"]::before,
            .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="24px"]::before { content: '24px'; }
            .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="28px"]::before,
            .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="28px"]::before { content: '28px'; }
            .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="32px"]::before,
            .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="32px"]::before { content: '32px'; }
            .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="36px"]::before,
            .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="36px"]::before { content: '36px'; }
            .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="48px"]::before,
            .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="48px"]::before { content: '48px'; }
            
            /* Line height dropdown labels */
            .ql-snow .ql-picker.ql-lineHeight .ql-picker-label {
              padding-right: 18px; /* keep arrow next to, not over, the text */
              min-width: 90px;
            }
            .ql-snow .ql-picker.ql-lineHeight .ql-picker-label::before,
            .ql-snow .ql-picker.ql-lineHeight .ql-picker-item::before {
              content: attr(data-value);
            }
            .ql-snow .ql-picker.ql-lineHeight .ql-picker-label[data-value="1.0"]::before,
            .ql-snow .ql-picker.ql-lineHeight .ql-picker-item[data-value="1.0"]::before { content: 'Single (1.0)'; }
            .ql-snow .ql-picker.ql-lineHeight .ql-picker-label[data-value="1.15"]::before,
            .ql-snow .ql-picker.ql-lineHeight .ql-picker-item[data-value="1.15"]::before { content: '1.15'; }
            .ql-snow .ql-picker.ql-lineHeight .ql-picker-label[data-value="1.5"]::before,
            .ql-snow .ql-picker.ql-lineHeight .ql-picker-item[data-value="1.5"]::before { content: '1.5'; }
            .ql-snow .ql-picker.ql-lineHeight .ql-picker-label[data-value="2.0"]::before,
            .ql-snow .ql-picker.ql-lineHeight .ql-picker-item[data-value="2.0"]::before { content: 'Double (2.0)'; }
            .ql-snow .ql-picker.ql-lineHeight .ql-picker-label[data-value="2.5"]::before,
            .ql-snow .ql-picker.ql-lineHeight .ql-picker-item[data-value="2.5"]::before { content: '2.5'; }
          `}
          </style>
          <ReactQuill
            theme="snow"
            value={content}
            onChange={(val) => {
              setContent(val);
              setHasUserEdited(true);
            }}
            modules={quillModules} style={{ width: "100%", height: "200px" }}
            formats={[
              'header', 'font', 'size', 'lineHeight', 'color', 'background',
              'bold', 'italic', 'underline', 'strike', 'blockquote',
              'list', 'bullet', 'indent',
              'link', 'image', 'align', 'code-block'
            ]}
          />
          <style>
            {`            .ql-editor p {
                margin-bottom: 0 !important;
              }
            `}
          </style>
        </Box>
      </Box>

      <Box display="flex" flexWrap="wrap" gap={1} mt={8}>
        <Button
          variant="text"
          component="label"
          startIcon={<AttachFileIcon />}
          sx={{
            textTransform: "none",
            mr: 2,
            color: "#2B80B6 !important",
            fontWeight: 500,
            padding: "4px 20px",
            fontSize: "13px",
            borderRadius: "0",
            fontFamily: "Fira Sans, Helvetica Neue, Helvetica, Roboto, Arial, sans-serif",
            marginBottom: 4,
            border: 0,
            boxShadow: 'none',
            background: 'none',
            '&:hover': {
              backgroundColor: '#f0f7ff',
              boxShadow: 'none',
            },
          }}
          style={{ marginRight: "10px", alignSelf: "flex-start" }}
        >
          {t('buttons:primary.attach_files')}
          <input type="file" hidden multiple onChange={handleFileChange} />
        </Button>
        {attachments.map((file, index) => (
          <Chip
            key={index}
            icon={<DescriptionIcon />}
            label={file.name}
            onDelete={() => removeAttachment(file)}
            deleteIcon={<DeleteIcon />}
            variant="outlined"
          />
        ))}
      </Box>

      <Box display="flex" alignItems="center" justifyContent="flex-end" gap={0}>
        <Button
          variant="text"
          onClick={handleSaveDraft}
          sx={{
            
            textTransform: "none",
            mr: 2,
            color: "#2B80B6 !important",
            fontWeight: 500,
            padding: "4px 20px",
            fontSize: "13px",
            borderRadius: "0",
            fontFamily: "Fira Sans, Helvetica Neue, Helvetica, Roboto, Arial, sans-serif",
            marginBottom: 4
          }}
          endIcon={<SaveIcon />}
        >
          {t('buttons:primary.save_draft')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          endIcon={<SendIcon />}
          onClick={handleSendEmail}
          sx={{ ...actionButtonStyles(), marginBottom: 4 }}
        >
          {t('buttons:primary.send_email')}
        </Button>
      </Box>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
      </Container>
    </Container>
  );
}

export default SendEmail;
